import { Routes } from '@angular/router';
import { LoginComponent } from './login/login.component';
import { UsersComponent } from './users/users.component';
import { AdminLayoutComponent } from './admin-layout/admin-layout.component';
import { TenantsComponent } from './customers/tenants/tenants.component';
import { OwnersComponent } from './customers/owners/owners.component';
import { AssignPermissionsComponent } from './users/assign-permissions/assign-permissions.component';
import { authGuard, permissionGuard } from './services/auth.guard';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { RegisterComponent } from './register/register.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { OwnerDetailsComponent } from './customers/owners/owner-details/owner-details.component';
import { OwnerDocumentsComponent } from './customers/owners/owner-documents/owner-documents.component';
import { TaxInfoComponent } from './customers/owners/tax-info/tax-info.component';
import { OwnerBankDetailsComponent } from './customers/owners/owner-bank-details/owner-bank-details.component';
import { OwnerLocalContactPersonComponent } from './customers/owners/owner-local-contact-person/owner-local-contact-person.component';
import { OwnerAdditionalInfoComponent } from './customers/owners/owner-additional-info/owner-additional-info.component';
import { PropertiesListComponent } from './properties/properties-list/properties-list.component';
import { PropertiesViewComponent } from './properties/properties-view/properties-view.component';
import { PropertyEditComponent } from './properties/property-edit/property-edit.component';
import { PropertyInvoiceComponent } from './properties/property-invoice/property-invoice.component';
import { EmailVerifyComponent } from './email-verify/email-verify.component';
import { ChangePasswordComponent } from './users/change-password/change-password.component';
import { TicketsComponent } from './tickets/tickets.component';

//resolvers
import { PropertiesResolver, PropertyAmenities, PropertyStatesResolver } from './resolvers/properties.resolver';
import { OwnerBillsComponent } from './customers/owners/owner-bills/owner-bills.component';
import { USER_PERMISSIONS } from '../app/app.constants';
import { LeadsListComponent } from './leads/leads-list/leads-list.component';
import { BankAccountsListComponent } from './bank-accounts/bank-accounts-list/bank-accounts-list.component';
import { LeadViewComponent } from './leads/lead-view/lead-view.component';
import { TasksListComponent } from './tasks/tasks-list/tasks-list.component';
import { TaskViewComponent } from './tasks/task-view/task-view.component';
import { ActivityReportComponent } from './tasks/activity-report/activity-report.component';
import { TaskRecurrencesComponent } from './tasks/task-recurrences/task-recurrences.component';
import { InspectionComponent } from './inspections/inspection/inspection.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { CreateInspectionComponent } from './inspections/create-inspection/create-inspection.component';
import { CreateNotificationsComponent } from './notifications/create-notifications/create-notifications.component';
import { BillCategoriesComponent } from './bill-categories/bill-categories.component';
import { ItemsComponent } from './items/items.component';
import { OwnerReportsComponent } from './reports/owner-reports/owner-reports.component';
import { CompanyReportsComponent } from './reports/company-reports/company-reports.component';
import { InspectionReportsComponent } from './reports/inspection-reports/inspection-reports.component';
import { AccountsReportsComponent } from './reports/accounts-reports/accounts-reports.component';
import { LeadsReportsComponent } from './reports/leads-reports/leads-reports.component';
import { EnquiriesReportsComponent } from './reports/enquiries-reports/enquiries-reports.component';
import { PropertyReportsComponent } from './reports/property-reports/property-reports.component';
import { LeaseReportsComponent } from './reports/lease-reports/lease-reports.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { TemplatesComponent } from './templates/templates.component';
import { ViewInspectionComponent } from './inspections/view-inspection/view-inspection.component';
import { EnquiriesListComponent } from './enquiries/enquiries-list/enquiries-list.component';
import { EnquiryViewComponent } from './enquiries/enquiry-view/enquiry-view.component';
import { EnquiryEditComponent } from './enquiries/enquiry-edit/enquiry-edit.component';
import { ProfileComponent } from './users/profile/profile.component';


export const routes: Routes = [
    { path: '', redirectTo: '/login', pathMatch: 'full' },
    { path: 'login', component: LoginComponent },
    { path: 'forgot-password', component : ForgotPasswordComponent },
    { path: 'register' , component: RegisterComponent },
    { path: 'reset-password/:token' , component : ResetPasswordComponent },
    { path: 'email-verify/:token', component : EmailVerifyComponent },
    { path: 'no-access' , component : NotFoundComponent },
    { path: 'admin', component: AdminLayoutComponent, canMatch: [authGuard],
      children: [
        {
          path: '', redirectTo: 'properties/properties-list', pathMatch: 'full'
        },{
          path: 'dashboard', component: DashboardComponent,
        },
        { path: 'users', component: UsersComponent ,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.USERS.USERS_PAGE },
        },
        { path: 'tenants', component : TenantsComponent,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.TENANTS.TENANTS_PAGE },
          resolve : {
            data : PropertiesResolver,
            states : PropertyStatesResolver
          }
         },
        { path: 'owners', component : OwnersComponent ,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.OWNERS.OWNERS_PAGE },
          resolve : {
            data : PropertiesResolver,
            states : PropertyStatesResolver
          }
        },
        { path: 'assign_permissions' , component : AssignPermissionsComponent,
          data : { PERMISSION : USER_PERMISSIONS.PERMISSIONS.PERMISSIONS_PAGE },
          canMatch : [permissionGuard],
         },

        { path: 'owner-details/overview/:type/:id', component : OwnerDetailsComponent,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.OWNERS.OWNERS_VIEW },
         },
        { path: 'owner-details/documents/:type/:id', component : OwnerDocumentsComponent,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.USER_INFO.USER_DOCUMENTS_TAB },
         },
        { path: 'owner-details/tax-info/:type/:id', component : TaxInfoComponent,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.USER_INFO.USER_LEASE_TAB },
         },
        { path: 'owner-details/bills/:type/:id', component : OwnerBillsComponent,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.USER_INFO.USER_BILLS_TAB },
         },
        { path: 'owner-details/bank-details/:type/:id', component : OwnerBankDetailsComponent,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.USER_INFO.USER_BANK_TAB },
         },
        { path: 'owner-details/local-contact/:type/:id', component : OwnerLocalContactPersonComponent,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.USER_INFO.USER_LOCAL_TAB },
         },
        { path: 'owner-details/additional-info/:type/:id', component : OwnerAdditionalInfoComponent,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.USER_INFO.USER_INFO_TAB },
         },
        { path: 'properties/properties-list', component : PropertiesListComponent,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.PROPERTIES.PROPERTIES_PAGE },
          resolve : {
            data : PropertiesResolver
          }
        },
        { path: 'properties/property-view/:id/:activeId' , component : PropertiesViewComponent,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.PROPERTIES.PROPERTY_VIEW },
         },
        { path: 'properties/property-edit/:id', component : PropertyEditComponent ,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.PROPERTIES.PROPERTY_EDIT },
          resolve : {
            data : PropertyAmenities
          }
        },
        { path: 'inspections' , component : InspectionComponent ,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.INSPECTIONS.INSPECTION_PAGE }
        },
        { path: 'create-inspection/:id' , component : CreateInspectionComponent ,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.INSPECTIONS.INSPECTION_START }
        },
        {
          path : 'view-inspection/:id/:property_id' , component : ViewInspectionComponent,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.INSPECTIONS.INSPECTION_REPORT_VIEW }
        },
        { path: 'service-requests' , component : TicketsComponent,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.SERVICE_REQUEST.SERVICE_REQUEST_PAGE }
        },
        { path: 'create-notifications' , component : CreateNotificationsComponent,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.NOTIFICATIONS.NOTIFICATIONS_PAGE }
        },
        { path: 'bill-categories', component: BillCategoriesComponent,
          canMatch: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.BILL_CATEGORIES.BILL_CATEGORIES_PAGE }
        },
        { path: 'items', component: ItemsComponent,
          canMatch: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.ITEMS.ITEMS_PAGE }
        },
        { path: 'change-password', component : ChangePasswordComponent },
        { path: 'profile', component : ProfileComponent },
        { path: 'properties/invoices', component : PropertyInvoiceComponent },
        { path: 'reports/owner-reports' , component :  OwnerReportsComponent,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.REPORTS.REPORTS_OWNER_PAGE },
          resolve : {
            data : PropertiesResolver
          }
        },
        { path : 'reports/company-reports', component : CompanyReportsComponent,
          canMatch : [permissionGuard],
          data : { PERMISSION : USER_PERMISSIONS.REPORTS.REPORTS_COMPANY_PAGE },
          resolve : {
            data : PropertiesResolver
          }
        },
        { path: 'reports/inspections', component: InspectionReportsComponent,
          canMatch: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.REPORTS.REPORTS_INSPECTION_PAGE }
        },
        { path: 'reports/accounts', component: AccountsReportsComponent,
          canMatch: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.REPORTS.REPORTS_ACCOUNTS_PAGE },
          resolve: {
            data: PropertiesResolver
          }
        },
        { path: 'reports/leads', component: LeadsReportsComponent,
          canActivate: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.REPORTS.REPORTS_LEADS_PAGE }
        },
        { path: 'reports/enquiries', component: EnquiriesReportsComponent,
          canActivate: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.REPORTS.REPORTS_ENQUIRIES_PAGE }
        },
        { path: 'reports/properties', component: PropertyReportsComponent,
          canMatch: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.REPORTS.REPORTS_PROPERTIES_PAGE }
        },
        { path: 'reports/leases', component: LeaseReportsComponent,
          canMatch: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.REPORTS.REPORTS_LEASES_PAGE }
        },
        { path: 'enquiries', component: EnquiriesListComponent,
          canActivate: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.ENQUIRIES.ENQUIRY_PAGE }
        },
        { path: 'enquiries/view/:id', component: EnquiryViewComponent,
          canActivate: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.ENQUIRIES.ENQUIRY_PAGE }
        },
        { path: 'enquiries/edit/:id', component: EnquiryEditComponent,
          canActivate: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.ENQUIRIES.ENQUIRY_EDIT }
        },
        { path: 'leads', component: LeadsListComponent,
          canActivate: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.LEADS.LEADS_PAGE }
        },
        { path: 'leads/view/:id', component: LeadViewComponent,
          canActivate: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.LEADS.LEADS_PAGE }
        },
        { path: 'tasks', component: TasksListComponent,
          canActivate: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.TASKS.TASKS_PAGE }
        },
        { path: 'tasks/view/:id', component: TaskViewComponent,
          canActivate: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.TASKS.TASKS_PAGE }
        },
        { path: 'tasks/activity', component: ActivityReportComponent,
          canMatch: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.TASKS.TASKS_REPORTS }
        },
        { path: 'tasks/recurrences', component: TaskRecurrencesComponent,
          canMatch: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.TASKS.TASKS_MANAGE_TEMPLATES }
        },
        { path: 'bank-accounts', component: BankAccountsListComponent,
          canActivate: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.BANK_ACCOUNTS.BANK_ACCOUNTS_PAGE }
        },
        { path: 'templates', component: TemplatesComponent,
          canMatch: [permissionGuard],
          data: { PERMISSION: USER_PERMISSIONS.TEMPLATES.TEMPLATES_PAGE }
        }
    ]
    },
    { path: '**', redirectTo: '/login' }
];
